<div class="mb-10 max-w-4xl mx-auto px-4 sm:px-6 mt-4">
  <div class="mb-3">
    <app-btn-back routeTo="/groups"></app-btn-back>
  </div>

  <div class="mb-6">
    <h1 class="text-2xl font-bold text-gray-900 tracking-tight">Organization requests</h1>
    <p class="text-xs text-blueGray-500 mt-1">
      Check each organization's details and proof of registration. Approving creates the organization with the
      requester as its administrator and emails them; rejecting emails them your reason.
    </p>
  </div>

  @if (isPlatformAdmin === null) {
  <p class="text-xs text-blueGray-500"><i class="fas fa-circle-notch fa-spin mr-1.5"></i>Checking access…</p>
  } @else if (!isPlatformAdmin) {
  <div class="p-5 rounded-xl border border-blueGray-200 bg-white text-xs text-blueGray-600">
    <i class="fas fa-lock mr-1.5 text-blueGray-400"></i>Only AtChurch platform admins can review organization requests.
  </div>
  } @else {
  @let all = (requests$ | async) || [];
  <div class="flex items-center justify-between mb-3">
    <span class="text-xs font-semibold text-blueGray-600">{{ pendingCount(all) }} waiting for review</span>
    <button type="button" (click)="toggleShowReviewed()"
      class="text-xs font-semibold text-primary-600 hover:text-primary-800 cursor-pointer">
      {{ showReviewed ? 'Show only pending' : 'Also show reviewed' }}
    </button>
  </div>

  <div class="space-y-3">
    @for (r of visible(all); track r.id) {
    <div class="bg-white rounded-xl border border-blueGray-200 overflow-hidden">
      <button type="button" (click)="toggleOpen(r)" [attr.aria-expanded]="openRequestId === r.id"
        class="w-full p-4 flex items-center justify-between gap-3 text-left hover:bg-blueGray-50 cursor-pointer">
        <div class="min-w-0">
          <span class="text-sm font-bold text-blueGray-800 block truncate">{{ r.name }}</span>
          <span class="text-[11px] text-blueGray-500">
            {{ typeLabel(r.orgType) }} · {{ r.country }} · from {{ r.requesterName || r.requesterEmail }} ·
            {{ r.created?.toDate() | date: 'mediumDate' }}
          </span>
        </div>
        <span class="flex items-center gap-2 shrink-0">
          @switch (r.status) {
          @case ('pending') {
          <span class="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-100">Pending</span>
          }
          @case ('approved') {
          <span class="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-100">Approved</span>
          }
          @case ('rejected') {
          <span class="px-2 py-0.5 rounded text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-100">Rejected</span>
          }
          }
          <i class="fas text-[10px] text-blueGray-400" [class.fa-chevron-down]="openRequestId !== r.id"
            [class.fa-chevron-up]="openRequestId === r.id"></i>
        </span>
      </button>

      @if (openRequestId === r.id) {
      <div class="px-4 pb-4 pt-1 border-t border-blueGray-100 space-y-3 text-xs text-blueGray-700">
        <div>
          <span class="text-[10px] uppercase font-bold text-blueGray-400 block tracking-wider">Purpose</span>
          <p class="whitespace-pre-line">{{ r.purpose }}</p>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <span class="text-[10px] uppercase font-bold text-blueGray-400 block tracking-wider">Registration number</span>
            {{ r.registrationNumber }}
          </div>
          <div>
            <span class="text-[10px] uppercase font-bold text-blueGray-400 block tracking-wider">Contact</span>
            <a [href]="'mailto:' + r.contactEmail" class="text-primary-600 hover:underline">{{ r.contactEmail }}</a>
            @if (r.contactPhone) { · {{ r.contactPhone }} }
          </div>
          <div>
            <span class="text-[10px] uppercase font-bold text-blueGray-400 block tracking-wider">Requested by</span>
            {{ r.requesterName }} ({{ r.requesterEmail }})
          </div>
        </div>
        <div>
          <span class="text-[10px] uppercase font-bold text-blueGray-400 block tracking-wider mb-1">Documents</span>
          <div class="flex flex-wrap gap-2">
            @for (d of r.documents; track d.storagePath) {
            <button type="button" (click)="openDocument(d)"
              class="px-2.5 py-1 rounded-lg border border-blueGray-200 hover:border-primary-300 text-[11px] font-semibold text-blueGray-700 hover:text-primary-600 cursor-pointer">
              <i class="fas mr-1" [class.fa-file-pdf]="d.contentType === 'application/pdf'"
                [class.fa-file-image]="d.contentType !== 'application/pdf'"></i>{{ d.name }}
            </button>
            }
          </div>
        </div>

        @if (r.status === 'pending') {
        <div>
          <label [for]="'note-' + r.id" class="text-[10px] uppercase font-bold text-blueGray-400 block tracking-wider mb-1">
            Note to the requester (required when rejecting)
          </label>
          <textarea [id]="'note-' + r.id" [(ngModel)]="notes[r.id]" rows="2" maxlength="1000"
            class="w-full px-3 py-2 text-xs rounded-xl border border-blueGray-300 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"></textarea>
        </div>
        <div class="flex justify-end gap-2">
          <button type="button" (click)="review(r, false)" [disabled]="reviewingId === r.id"
            class="px-3 py-1.5 rounded-xl border border-rose-200 text-rose-700 hover:bg-rose-50 disabled:opacity-50 text-xs font-semibold cursor-pointer">
            Reject
          </button>
          <button type="button" (click)="review(r, true)" [disabled]="reviewingId === r.id"
            class="px-3 py-1.5 rounded-xl bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white text-xs font-semibold cursor-pointer">
            {{ reviewingId === r.id ? 'Saving…' : 'Approve & create' }}
          </button>
        </div>
        } @else {
        <p class="text-[11px] text-blueGray-500">
          {{ r.status === 'approved' ? 'Approved' : 'Rejected' }} {{ r.reviewedAt?.toDate() | date: 'medium' }}
          @if (r.reviewNote) { · Note: {{ r.reviewNote }} }
          @if (r.organizationId) {
          · <a [routerLink]="['/management', r.organizationId]" class="text-primary-600 hover:underline">Open organization</a>
          }
        </p>
        }
      </div>
      }
    </div>
    } @empty {
    <div class="p-6 rounded-xl border border-dashed border-blueGray-200 bg-white text-center text-xs text-blueGray-500">
      {{ showReviewed ? 'No organization requests yet.' : 'Nothing waiting for review.' }}
    </div>
    }
  </div>
  }
</div>
